import { cloneElement, isValidElement } from "react";
import { createPortal } from "react-dom";

import { colorsPalettes, elevation } from "@similarweb/styles";
import { DotsLoader } from "@similarweb/ui-components";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import type { IBrandAutocompleteComboboxProps, ITriggerFieldProps } from "./types";
import { useBrandAutocompleteCombobox } from "./useBrandAutocompleteCombobox";

const SELECTED_OPTION_BACKGROUND = colorsPalettes.bluegrey[100];

export const BrandAutocompleteCombobox = ({
    query,
    onSelect,
    dataAutomation,
    children,
    excludedBrands = [],
    disabled = false,
    width = "100%",
    dropdownWidth,
    showTypedAsOption = false,
}: IBrandAutocompleteComboboxProps) => {
    const {
        isOpen,
        isLoading,
        options,
        emptyState,
        activeIndex,
        position,
        anchorRef,
        portalRef,
        handleSelect,
        handleKeyDownCapture,
    } = useBrandAutocompleteCombobox({
        query,
        onSelect,
        excludedBrands,
        disabled,
        showTypedAsOption,
    });

    const trigger = isValidElement<ITriggerFieldProps>(children)
        ? cloneElement(children, {
              endAddon: isLoading ? (
                  <DotsLoader data-automation={`${dataAutomation}-autocomplete-input-loader`} />
              ) : (
                  children.props.endAddon
              ),
          })
        : children;

    return (
        <Box
            ref={anchorRef}
            width={width}
            data-automation={`${dataAutomation}-autocomplete`}
            onKeyDownCapture={handleKeyDownCapture}
        >
            {trigger}
            {isOpen && position
                ? createPortal(
                      <Box
                          ref={portalRef}
                          role="listbox"
                          position="fixed"
                          top={`${position.top}px`}
                          left={`${position.left}px`}
                          width={dropdownWidth ?? `${position.width}px`}
                          background={colorsPalettes.carbon[0]}
                          border={`1px solid ${colorsPalettes.carbon[50]}`}
                          borderRadius="3px"
                          boxShadow={elevation.Z2}
                          maxHeight="300px"
                          overflowY="auto"
                          zIndex={1300}
                          data-automation={`${dataAutomation}-autocomplete-dropdown`}
                          data-react-aria-top-layer="true"
                      >
                          {emptyState ??
                              options.map((option, index) => (
                                    <Flex
                                        key={option.id}
                                        role="option"
                                        aria-selected={index === activeIndex}
                                        height="44px"
                                        paddingX="16px"
                                        paddingY="6px"
                                        alignItems="center"
                                        boxSizing="border-box"
                                        cursor="auto"
                                        fontVariant="body2"
                                        color={colorsPalettes.carbon[500]}
                                        background={index === activeIndex ? SELECTED_OPTION_BACKGROUND : "transparent"}
                                        whenHover={{ background: SELECTED_OPTION_BACKGROUND }}
                                        onMouseDown={(event) => event.preventDefault()}
                                        onClick={() => handleSelect(option.brand)}
                                        data-automation={`${dataAutomation}-autocomplete-option-${option.id}`}
                                    >
                                        {option.label}
                                    </Flex>
                                ))}
                      </Box>,
                      document.body,
                  )
                : null}
        </Box>
    );
};
